import { UnreachableError } from 'base/helpers';
import {
  addPromiseToCache,
  CacheState,
  LibraryCache,
} from 'base/library_cache';
import { randomChoice, randomInteger } from 'base/random';
import { randomHexColorCode, suggestColor } from 'base/styleguide/color';
import {
  assertShadeKey,
  compileChromatics,
  compileNeutral,
  generateColorVariables,
  generateSchemes,
  getShadeKey,
} from 'base/styleguide/concept';
import { isValidFontWeight } from 'base/styleguide/fonts';
import {
  extractSectionMetadata,
  getSchemeShadeKeys,
} from 'base/styleguide/helpers';
import {
  aConcept,
  aDesignScheme,
  aPrivateImageRef,
  CardStyle,
  CardStyleRadiusOption,
  Concept,
  ConceptState,
  DesignScheme,
  DesignSchemeState,
  TypographyTarget,
  UiStyle,
  UiStyleRadiusOption,
} from 'base/styleguide/model';
import {
  ColorVariable,
  CompiledChromaticColor,
  CompiledNeutralColor,
  CompiledScheme,
  parseShadeKey,
  ShadeKey,
} from 'base/styleguide/types';
import { LottiePlayer } from 'base/ui/lottie';
import { ToastQueue } from 'base/ui/toast';
import { BlocksService, FontFamilyMetadata } from 'blocks/blocks_service';
import { ProjectSession } from 'blocks/editor/session/session';
import { GetColorName } from 'hex-color-to-color-name';
import clamp from 'lodash/clamp';
import { action, computed, makeObservable, observable } from 'mobx';
import React from 'react';
import animatedStarsLottie from '../ai/animated_stars.lottie.json';
import { Assistant } from '../ai/assistant';
import toastStyles from '../ai/toasts.css';
import { pageAndAllSubpages, unrefSection } from '../model_helpers';
import { ProjectAssetStore } from './asset_store';
import { CARD_STYLES, CARD_STYLE_RADIUS_OPTIONS } from './card_styles';
import {
  BODY_FONT_COLLECTION_SLUGS,
  FontMetadataService,
  FONT_WEIGHTS_BODY,
  globalRemoteFontStore,
  HEADING_FONT_COLLECTION_SLUGS,
} from './fonts';
import {
  getPlaceholderImageUrl,
  ImageRegistry,
  populateImagesForPage,
  RenderedImage,
} from './images';
import { generateSchemeTemplates } from './style_application/concept_styleguide';
import {
  globalTypeSettingsOverrideStore,
  HEADING_FONT_WEIGHTS,
} from './type_settings';
import { getGlobalTypographyPanelStore } from './typography_panel';
import { UI_STYLES, UI_STYLE_RADIUS_OPTIONS } from './ui_styles';

const MAX_CONCEPTS = 8;

const CONCEPT_SHUFFLE_TOAST_GROUP = Symbol();

class ConceptsStore {
  @observable
  activeConceptIndex: number = 0;

  private renderedLogoCache: Map<
    string,
    CacheState<RenderedImage | undefined>
  > = observable.map([], { deep: false });

  fontMetadataService: FontMetadataService;

  constructor(
    private readonly session: ProjectSession,
    private readonly blocksService: BlocksService,
    private readonly library: LibraryCache,
    private readonly toastQueue: ToastQueue,
  ) {
    makeObservable(this);
    this.fontMetadataService = new FontMetadataService(blocksService);
  }

  @computed
  get projectAssetStore() {
    return new ProjectAssetStore(this.blocksService, this.session.projectToken);
  }

  @computed
  get imageRegistry() {
    return new ImageRegistry(this.projectAssetStore);
  }

  @computed
  get allConcepts() {
    return this.session.project.concepts;
  }

  setGalleryPosition(uuid: string, galleryPosition: number | undefined) {
    const currentPosition = this.session.project.concepts.find(
      (c) => c.uuid === uuid,
    )?.galleryPosition;

    for (const concept of this.session.project.concepts) {
      if (concept.uuid === uuid) {
        concept.galleryPosition = galleryPosition;
      } else if (
        galleryPosition != null &&
        concept.galleryPosition === galleryPosition
      ) {
        concept.galleryPosition = currentPosition;
      }
    }
  }

  @action
  setActiveConceptByUuid(uuid: string) {
    const conceptIndex = this.session.project.concepts.findIndex(
      (c) => c.uuid === uuid,
    );
    if (conceptIndex === -1) {
      throw new Error('No such concept exists');
    }
    this.activeConceptIndex = conceptIndex;
  }

  @action
  createNewConcept(
    concept: Partial<ConceptState> & Pick<ConceptState, 'createdBy'>,
  ): ConceptAPI {
    if (this.session.project.concepts.length >= MAX_CONCEPTS) {
      throw new Error('Only 3 concepts allowed');
    }
    const newConcept = aConcept(concept);
    this.session.project.concepts.push(newConcept);

    const conceptApi = this.concepts.find(
      (c) => c.state.uuid == newConcept.uuid,
    );
    if (conceptApi == null) {
      throw new Error('Failed to find newly created concept');
    }

    return conceptApi;
  }

  deleteConcept(uuid: string) {
    const index = this.session.project.concepts.findIndex(
      (c) => c.uuid === uuid,
    );
    if (index === -1) return;

    this.session.project.concepts.splice(index, 1);
  }

  getRenderedLogo(theme: 'light' | 'dark') {
    const renderedLogo = this.renderedLogoCache.get(theme);
    if (renderedLogo != null) {
      return renderedLogo;
    }

    const logo = this.session.project.logo.current;
    const logoRef = logo?.state?.[theme].current;
    if (!logoRef || !this.imageRegistry) {
      return undefined;
    }

    const renderImagePromise = (async () => {
      const renderedLogo = await this.imageRegistry.renderImage(logoRef)
        .promise;

      if (renderedLogo) {
        // Wait for the image to be loaded by the browser before returning.
        const im = new Image();
        const loadPromise = new Promise((resolve, reject) => {
          im.onload = resolve;
          im.onerror = reject;
        });
        im.src = renderedLogo.src;
        await loadPromise;
      }

      return renderedLogo;
    })();
    return addPromiseToCache(this.renderedLogoCache, theme, renderImagePromise);
  }

  async uploadLogo(file: File, theme: 'light' | 'dark') {
    const image = await this.blocksService.uploadImage(
      this.session.projectToken,
      file,
    );
    if (image.assets.length < 1) {
      throw new Error('uploadImage returned no assets');
    }
    const asset = image.assets[0]!;
    const id = asset.id;

    // Don't replace the image until it's been fetched and cached by the
    // browser, so we can switch over to it instantly.
    const url = asset.files[2].url;
    if (url) {
      const im = new Image();
      const loadPromise = new Promise((resolve, reject) => {
        im.onload = resolve;
        im.onerror = reject;
      });
      im.src = url;
      await loadPromise;
    }

    if (this.session.project.logo.current == null) {
      this.session.project.logo.replace({
        light: { current: undefined },
        dark: { current: undefined },
        height: 32,
      });
    }

    this.session.project.logo.current![theme].replace(aPrivateImageRef(id));

    return asset;
  }

  deleteLogo(theme: 'light' | 'dark') {
    // TODO: eventually we should clean up the storage of the file here.
    if (this.session.project.logo.current == null) {
      this.session.project.logo.replace({
        light: { current: undefined },
        dark: { current: undefined },
        height: 32,
      });
    }

    this.session.project.logo.current![theme].replace(undefined);
    this.renderedLogoCache.delete(theme);
  }

  @computed
  get logo() {
    return this.session.project.state.logo.current;
  }

  @action
  setLogoHeight(height: number) {
    if (this.session.project.logo.current == null) {
      this.session.project.logo.replace({
        light: { current: undefined },
        dark: { current: undefined },
        height: 32,
      });
    }

    this.session.project.logo.current!.height = height;
  }

  @computed
  get activeConcept(): ConceptAPI {
    if (this.concepts.length === 0) {
      throw new Error('Project has no concepts');
    }
    return this.concepts[
      clamp(this.activeConceptIndex, 0, this.concepts.length - 1)
    ];
  }

  @computed
  get concepts(): ReadonlyArray<ConceptAPI> {
    return this.session.project.concepts.map(
      (c) =>
        new ConceptAPI(
          this.session,
          this.blocksService,
          this.fontMetadataService,
          this.library,
          this.toastQueue,
          this.projectAssetStore,
          this.imageRegistry,
          c,
        ),
    );
  }
}
export type { ConceptsStore };

let globalConceptStore: ConceptsStore | null = null;

/** Initalize the global store of the current projects concepts. */
export function initGlobalConceptsStore(
  session: ProjectSession,
  blocksService: BlocksService,
  library: LibraryCache,
  toastQueue: ToastQueue,
) {
  globalConceptStore = new ConceptsStore(
    session,
    blocksService,
    library,
    toastQueue,
  );
}

/**
 * Get a store for tracking the concepts for the current project. Will error if
 * called before `initGlobalConceptStore` is called.
 */
export function getGlobalConceptsStore() {
  if (!globalConceptStore) {
    throw new Error('ConceptStore not initalized.');
  }
  return globalConceptStore;
}

export class ConceptAPI {
  constructor(
    private readonly session: ProjectSession,
    private readonly blocksService: BlocksService,
    private readonly fontMetadataService: FontMetadataService,
    private readonly library: LibraryCache,
    private readonly toastQueue: ToastQueue,
    private readonly projectAssetStore: ProjectAssetStore,
    private readonly imageRegistry: ImageRegistry,
    private readonly atom: Concept,
  ) {
    makeObservable(this);
    if (this.atom.chromatics.length === 0) {
      // Ensure there is at least one chromatic
      this.session.undo.doPermanently(() => {
        const hex = randomHexColorCode();
        this.atom.chromatics.push({
          hex: hex,
          name: GetColorName(hex),
          locked: false,
        });
      });
    }
  }

  @computed
  get state() {
    return this.atom.state;
  }

  @computed
  get chromatics(): ReadonlyArray<CompiledChromaticColor> {
    return compileChromatics(this.atom.state);
  }

  @computed
  get neutral(): CompiledNeutralColor {
    return compileNeutral(this.atom.state);
  }

  @computed
  get schemes(): ReadonlyArray<CompiledScheme> {
    return generateSchemes(this);
  }

  @computed
  get colorVariables(): ReadonlyMap<ColorVariable, string> {
    return generateColorVariables(this);
  }

  @action
  setHidden(hidden: boolean) {
    this.atom.hidden = hidden;
  }

  @action
  setDescription(description: string) {
    this.atom.description = description;
  }

  @action
  updateTheme(theme: 'light' | 'dark') {
    if (this.state.theme === theme) {
      return;
    }
    this.atom.theme = theme;
    this.invertHomepageSchemes();

    if (this.session.flags.get('enableDesignView')) {
      this.syncConceptToDesignSchemes();
    }
  }

  @action
  updateNeutralTint(chromaticIndex?: number) {
    this.atom.neutral.tintIndex = chromaticIndex;
  }

  @action
  updateNeutralLocked(locked: boolean) {
    this.atom.neutral.locked = locked;
  }

  @action
  updateChromaticName(chromaticIndex: number, name: string) {
    this.atom.chromatics[chromaticIndex].name = name;
  }

  @action
  updateChromaticHex(chromaticIndex: number, hex: string) {
    this.atom.chromatics[chromaticIndex].name = GetColorName(hex);
    this.atom.chromatics[chromaticIndex].hex = hex;
  }

  @action
  updateChromaticLocked(chromaticIndex: number, locked: boolean) {
    this.atom.chromatics[chromaticIndex].locked = locked;
  }

  @action
  shuffleColors(minChromaticCount?: number) {
    this.shuffleChromatics(minChromaticCount);
    this.shuffleNeutral();
  }

  @action
  shuffleNeutral() {
    if (this.neutral.locked) return;

    // Randomly tint the neutral color. Need to take into account that no tint
    // is a valid option so we need to handle that case.
    let tintIndex = randomInteger(0, this.chromatics.length);
    this.updateNeutralTint(
      tintIndex === this.chromatics.length ? undefined : tintIndex,
    );
  }

  @action
  shuffleChromatics(chromaticCount?: number) {
    if (chromaticCount != null) {
      if (chromaticCount > this.chromatics.length) {
        for (let i = this.chromatics.length; i < chromaticCount; i++) {
          this.addChromatic();
        }
      } else if (chromaticCount < this.chromatics.length) {
        for (let i = this.chromatics.length - 1; i >= chromaticCount; i--) {
          if (this.chromatics[i].locked) {
            continue;
          }

          this.removeChromatic(i);
        }
      }
    }

    for (const [index, chromatic] of this.chromatics.entries()) {
      if (chromatic.locked) continue;

      const newChromatic =
        index === 0 ? randomHexColorCode() : suggestColor(this, index);

      this.updateChromaticHex(index, newChromatic);
    }
  }

  @action
  shuffleChromatic(index: number) {
    this.updateChromaticHex(index, suggestColor(this, index));
  }

  @action
  shuffleSchemes() {
    // We compare the current header scheme to determine if we're already using it
    // Little hack here to invert the schemes before we do this as it all operates
    // in light mode
    if (this.state.theme === 'dark') {
      this.invertHomepageSchemes();
    }
    const sections = this.session.project.homePage.sections;
    const headerSectionId =
      sections[1] &&
      unrefSection(sections[1], this.session.project)?.element?.current?.id;
    if (headerSectionId == null) {
      return; // Nothing to do here
    }

    const headerSectionScheme = this.getShadeKeyByElementId(headerSectionId);

    const schemesTemplate = randomChoice(
      ...generateSchemeTemplates(this).filter(
        (schemeTemplate) => schemeTemplate.header[1] !== headerSectionScheme,
      ),
    );

    for (const [
      index,
      sref,
    ] of this.session.project.homePage.sections.entries()) {
      const section = unrefSection(sref, this.session.project);
      const id = section?.element?.current?.id;
      if (id == null) {
        continue;
      }

      if (index < 3) {
        this.atom.schemeOverrideByElementId.set(
          id,
          schemesTemplate.header[index],
        );
      } else if (index > sections.length - 3) {
        this.atom.schemeOverrideByElementId.set(id, schemesTemplate.footer);
      } else {
        const offsetIndex = index - 3;
        this.atom.schemeOverrideByElementId.set(
          id,
          schemesTemplate.middle[offsetIndex % schemesTemplate.middle.length],
        );
      }
    }

    if (this.state.theme === 'dark') {
      // The generated schemes are built for the light theme, for dark we need to
      // invert them.
      this.invertHomepageSchemes();
    }

    if (this.session.flags.get('enableDesignView')) {
      this.syncConceptToDesignSchemes();
    }
  }

  @action
  addChromatic() {
    if (this.atom.chromatics.length >= 5) return;

    const suggestedColor = suggestColor(this, this.atom.chromatics.length);

    this.atom.chromatics.push({
      hex: suggestedColor,
      name: GetColorName(suggestedColor),
      locked: false,
    });
  }

  @action
  removeChromatic(index: number) {
    if (index === 0) return; // Cannot remove the primary color
    this.atom.chromatics.splice(index, 1);
    const chromaticIndex = index + 1;
    for (const [
      elementId,
      scheme,
    ] of this.atom.schemeOverrideByElementId.entries()) {
      const shadeDescriptor = parseShadeKey(scheme);
      if (
        shadeDescriptor == null ||
        shadeDescriptor.palette === 'neutral' ||
        shadeDescriptor.chromatic < chromaticIndex
      ) {
        continue;
      }

      if (shadeDescriptor.chromatic === chromaticIndex) {
        // Invalidate the scheme override for the deleted chromatic
        this.atom.schemeOverrideByElementId.delete(elementId);
      } else {
        // Otherwise, we need to bump them down one chromatic
        this.atom.schemeOverrideByElementId.set(
          elementId,
          getShadeKey({
            ...shadeDescriptor,
            chromatic: shadeDescriptor.chromatic - 1,
          }),
        );
      }
    }

    if (this.session.flags.get('enableDesignView')) {
      this.syncConceptToDesignSchemes();
    }
  }

  @action
  clearPlaceholderImages() {
    this.atom.images.clear();
  }

  @action
  addPlaceholderImage(key: string, placeholderId: string) {
    this.atom.images.set(key, {
      type: 'placeholder',
      id: placeholderId,
    });
  }

  @action
  setSchemeForSection(elementId: string, shadeKey: ShadeKey) {
    this.atom.schemeOverrideByElementId.set(elementId, shadeKey);

    if (this.session.flags.get('enableDesignView')) {
      this.syncConceptToDesignSchemes();
    }
  }

  setFontFamily(target: TypographyTarget, fontFamily: FontFamilyMetadata) {
    this.atom.fontFamilies[target].slug = fontFamily.slug;
    if (target !== 'heading') {
      return;
    }

    if (
      isValidFontWeight(
        HEADING_FONT_WEIGHTS[this.atom.typeSettings.weight],
        fontFamily,
      )
    ) {
      return;
    }

    const validWeight = (['regular', 'medium', 'bold'] as const).find(
      (weight) => isValidFontWeight(HEADING_FONT_WEIGHTS[weight], fontFamily),
    );
    if (validWeight) {
      this.atom.typeSettings.weight = validWeight;
    }
  }

  getFontFamily(target: TypographyTarget) {
    return this.atom.fontFamilies[target].state;
  }

  getFontWeights(target: TypographyTarget) {
    switch (target) {
      case 'heading':
        return [
          HEADING_FONT_WEIGHTS[
            globalTypeSettingsOverrideStore.typeSettingsOverrides.weight ??
              this.atom.typeSettings.weight
          ],
        ];
      case 'body':
        return FONT_WEIGHTS_BODY;
      default:
        throw new UnreachableError(target);
    }
  }

  lockFontFamily(target: TypographyTarget, locked: boolean) {
    this.atom.fontFamilies[target].locked = locked;
  }

  getLikedFontFamilies(target: TypographyTarget) {
    return this.atom.fontFamilies[target].liked.state;
  }

  isFontFamilyLiked(target: TypographyTarget, slug: string) {
    return this.getLikedFontFamilies(target).includes(slug);
  }

  setLikedFontFamilyState(
    target: TypographyTarget,
    slug: string,
    isLiked: boolean,
  ) {
    const existingIndex = this.getLikedFontFamilies(target).indexOf(slug);
    if (isLiked && existingIndex === -1) {
      this.atom.fontFamilies[target].liked.push(slug);
    } else if (!isLiked && existingIndex !== -1) {
      this.atom.fontFamilies[target].liked.splice(existingIndex, 1);
    }
  }

  async pickRandomFontFamily(
    target: TypographyTarget,
  ): Promise<FontFamilyMetadata> {
    const likedFontFamilies = this.getLikedFontFamilies(target);

    if (likedFontFamilies.length === 0 || target === 'body') {
      // No liked fonts, or we're picking a body font where the liked fonts
      // could result in font pairings that don't work well together. Pick one
      // from one of our curated collections.
      const [popular] =
        await this.fontMetadataService.listFontFamiliesFromCollections(
          target === 'heading'
            ? HEADING_FONT_COLLECTION_SLUGS
            : BODY_FONT_COLLECTION_SLUGS,
          1,
          target === 'body' ? ['sans-serif'] : undefined,
        );
      if (popular == null) {
        throw new Error('No popular font family found');
      }
      return popular.fontFamily;
    }

    const globalTypographyPanelStore = getGlobalTypographyPanelStore();

    // Pick a font similar to the ones the user has liked.
    const currentFontFamily = this.getFontFamily(target);
    const recommendedFontFamilies =
      globalTypographyPanelStore.getRecommendedFontFamilies(target) ??
      (await globalTypographyPanelStore.reloadRecommendedFontFamilies(target));

    const currentIndex = recommendedFontFamilies.findIndex(
      (f) => f.fontFamily.slug === currentFontFamily.slug,
    );
    const nextIndex =
      currentIndex === -1
        ? 0
        : (currentIndex + 1) % recommendedFontFamilies.length;

    const recommended = recommendedFontFamilies[nextIndex];

    return recommended.fontFamily;
  }

  async pickAndLoadRandomFontFamily(target: TypographyTarget) {
    const fontFamilyMetadata = await this.pickRandomFontFamily(target);
    const weights = this.getFontWeights(target);
    await globalRemoteFontStore.loadRemoteFontFamily(
      fontFamilyMetadata.slug,
      weights,
      {
        blocksService: this.blocksService,
      },
    ).promise;
    return fontFamilyMetadata;
  }

  @action
  setFontOnboardingCompleted(value: boolean) {
    this.atom.fontOnboardingCompleted = value;
  }

  @observable
  isShuffling: boolean = false;

  @observable
  isShufflingHeading: boolean = false;

  @observable
  isShufflingBody: boolean = false;

  @observable
  isShufflingColors: boolean = false;

  async shuffle(opts: {
    typography?: { heading?: boolean; body?: boolean };
    theme?: boolean;
    colors?: boolean;
    images?: boolean;
    schemes?: boolean;
    uiStyle?: boolean;
    cardStyle?: boolean;

    overrideLocked?: boolean;
    showProgressToast?: boolean;
    showDoneToast?: boolean;
  }) {
    if (this.isShuffling) return;

    this.isShuffling = true;

    const disposeToast = opts.showProgressToast
      ? this.toastQueue.addPersistent(() => ({
          theme: 'light',
          label: 'Generating...',
          customLeft: (
            <div className={toastStyles.icon}>
              <LottiePlayer animationData={animatedStarsLottie} />
            </div>
          ),
        }))
      : null;

    // Part 1: prepare values we'll be applying to the concept (asynchronously).
    let newHeadingFontFamilyPromise: Promise<FontFamilyMetadata> | undefined;
    let newBodyFontFamilyPromise: Promise<FontFamilyMetadata> | undefined;

    if (
      opts.typography?.heading &&
      (!this.getFontFamily('heading').locked || opts.overrideLocked)
    ) {
      this.isShufflingHeading = true;
      newHeadingFontFamilyPromise = this.pickAndLoadRandomFontFamily('heading');
    }
    if (
      opts.typography?.body &&
      (!this.getFontFamily('body').locked || opts.overrideLocked)
    ) {
      this.isShufflingBody = true;
      newBodyFontFamilyPromise = this.pickAndLoadRandomFontFamily('body');
    }

    if (opts.colors) {
      this.isShufflingColors = true;
    }

    let imagesToApply: Record<string, string> = {};

    if (opts.images) {
      imagesToApply = await populateImagesForPage(
        this.session.project.homePage,
        this.session,
        this,
        this.library,
        { overwrite: true },
      );
    }

    // For each unique placeholder image ID we're applying, create a promise
    // that resolves once it's been loaded and cached by the browser.
    const imagePromises = [...new Set(Object.values(imagesToApply))].map(
      (placeholderId) => {
        const url = getPlaceholderImageUrl(placeholderId);
        return this.imageRegistry.preloadImage(url).promise;
      },
    );

    let newUiStyle: UiStyle | undefined;
    let newUiStyleRadiusOption: UiStyleRadiusOption | undefined;

    let newCardStyle: CardStyle | undefined;
    let newCardStyleRadiusOption: CardStyleRadiusOption | undefined;

    if (opts.uiStyle && (!this.atom.uiStyleLocked || opts.overrideLocked)) {
      newUiStyle = randomChoice(...UI_STYLES);
      newUiStyleRadiusOption = randomChoice(...UI_STYLE_RADIUS_OPTIONS);
    }

    if (opts.cardStyle && (!this.atom.cardStyleLocked || opts.overrideLocked)) {
      newCardStyle = randomChoice(...CARD_STYLES);
      newCardStyleRadiusOption = randomChoice(...CARD_STYLE_RADIUS_OPTIONS);
    }

    const [newHeadingFontFamily, newBodyFontFamily] = await Promise.all([
      newHeadingFontFamilyPromise,
      newBodyFontFamilyPromise,
      ...imagePromises,
    ]);

    // Part 2: apply the new values to the concept (synchronously).
    if (newHeadingFontFamily) {
      this.setFontFamily('heading', newHeadingFontFamily);
    }
    if (newBodyFontFamily) {
      this.setFontFamily('body', newBodyFontFamily);
    }

    for (const [key, placeholderId] of Object.entries(imagesToApply)) {
      this.addPlaceholderImage(key, placeholderId);
    }

    if (opts.theme) {
      this.updateTheme(randomChoice('light', 'dark'));
    }

    if (opts.colors) {
      this.shuffleColors(randomInteger(2, 3));
    }

    if (opts.schemes) {
      this.shuffleSchemes();
    }

    if (newUiStyle) {
      this.setUiStyle(newUiStyle);
    }

    if (newUiStyleRadiusOption) {
      this.setUiStyleRadiusOption(newUiStyleRadiusOption);
    }

    if (newCardStyle) {
      this.setCardStyle(newCardStyle);
    }

    if (newCardStyleRadiusOption) {
      this.setCardStyleRadiusOption(newCardStyleRadiusOption);
    }

    disposeToast?.();

    if (opts.showDoneToast) {
      this.toastQueue.push({
        confetti: true,
        theme: 'light',
        label: 'Surprise!',
        group: CONCEPT_SHUFFLE_TOAST_GROUP,
      });
    }

    this.isShuffling = false;
    this.isShufflingHeading = false;
    this.isShufflingBody = false;
    this.isShufflingColors = false;
  }

  @action
  updateTypeSettings(delta: Partial<ConceptState['typeSettings']>) {
    if (delta.size != null) {
      this.atom.typeSettings.size = delta.size;
    }
    if (delta.weight != null) {
      this.atom.typeSettings.weight = delta.weight;
    }
    if (delta.style != null) {
      this.atom.typeSettings.style = delta.style;
    }
  }

  // TODO: dedupe some of this with logo stuff
  async uploadCustomImage(imageKey: string, file: File) {
    const asset = await this.projectAssetStore.uploadProjectImage(file);

    // Don't replace the image until the thumbnail has been fetched and cached
    // by the browser, so we can switch over to it instantly.
    await this.imageRegistry.preloadImage(asset.thumbnailUrl).promise;

    this.atom.images.set(imageKey, {
      type: 'private',
      id: asset.id,
      properties: {
        fit: undefined,
        position: undefined,
        ratio: undefined,
        crop: [0, 0, asset.width, asset.height],
      },
    });

    return asset;
  }

  clearImage(imageKey: string) {
    this.atom.images.delete(imageKey);
  }

  get uiStyle(): UiStyle {
    return this.atom.uiStyle ?? UiStyle.Default;
  }

  @action
  setUiStyle(uiStyle: UiStyle) {
    this.atom.uiStyle = uiStyle;
  }

  get uiStyleRadiusOption(): UiStyleRadiusOption {
    if (
      this.atom.uiStyleRadiusOption &&
      (UI_STYLE_RADIUS_OPTIONS as string[]).includes(
        this.atom.uiStyleRadiusOption,
      )
    ) {
      return this.atom.uiStyleRadiusOption as UiStyleRadiusOption;
    }
    return UiStyleRadiusOption.Radius0;
  }

  @action
  setUiStyleRadiusOption(option: UiStyleRadiusOption) {
    this.atom.uiStyleRadiusOption = option;
  }

  get uiStyleLocked(): boolean {
    return !!this.atom.uiStyleLocked;
  }

  @action
  setUiStyleLocked(locked: boolean) {
    this.atom.uiStyleLocked = locked;
  }

  get cardStyle(): CardStyle {
    return this.atom.cardStyle ?? CardStyle.Default;
  }

  @action
  setCardStyle(cardStyle: CardStyle) {
    this.atom.cardStyle = cardStyle;
  }

  get cardStyleRadiusOption(): CardStyleRadiusOption {
    if (
      this.atom.cardStyleRadiusOption &&
      (CARD_STYLE_RADIUS_OPTIONS as string[]).includes(
        this.atom.cardStyleRadiusOption,
      )
    ) {
      return this.atom.cardStyleRadiusOption as CardStyleRadiusOption;
    }
    return CardStyleRadiusOption.Radius0;
  }

  @action
  setCardStyleRadiusOption(option: CardStyleRadiusOption) {
    this.atom.cardStyleRadiusOption = option;
  }

  get cardStyleLocked(): boolean {
    return !!this.atom.cardStyleLocked;
  }

  @action
  setCardStyleLocked(locked: boolean) {
    this.atom.cardStyleLocked = locked;
  }

  getShadeKeyByElementId(elementId: string): ShadeKey {
    const shadeKey = this.state.schemeOverrideByElementId.get(elementId);
    if (shadeKey == null) {
      return this.state.theme === 'light'
        ? 'neutral-shade-0'
        : 'neutral-shade-6';
    }
    return assertShadeKey(shadeKey);
  }

  getCompiledSchemeByElementId(elementId: string): CompiledScheme {
    const shadeKey = this.getShadeKeyByElementId(elementId);
    const compiledScheme = this.schemes.find(
      (scheme) => getShadeKey(scheme) === shadeKey,
    );
    if (compiledScheme == null) {
      throw new Error(
        `Wasn't able to find compiled scheme for element ${elementId}`,
      );
    }
    return compiledScheme;
  }

  private invertHomepageSchemes() {
    for (const sref of this.session.project.homePage.sections) {
      const section = unrefSection(sref, this.session.project);
      const id = section?.element?.current?.id;
      if (id === undefined) {
        continue;
      }

      // Only invert for sections where schemes have been explicitly set.
      const scheme = this.atom.schemeOverrideByElementId.get(id);
      if (scheme === undefined) {
        continue;
      }

      const oldShade = scheme.match(/-(\d+)$/)?.[1];
      if (oldShade === undefined) {
        continue;
      }

      const newShade =
        (scheme.startsWith('neutral') ? 7 : 8) - parseInt(oldShade, 10);
      const newScheme = scheme.replace(/-\d+$/, `-${newShade}`);

      this.atom.schemeOverrideByElementId.set(id, assertShadeKey(newScheme));
    }
  }

  @computed
  get designSchemes(): ReadonlyArray<DesignScheme> {
    return this.atom.designSchemes;
  }

  @action
  addDesignScheme(state: DesignSchemeState) {
    this.atom.designSchemes.push(state);
  }

  @action
  deleteDesignScheme(designScheme: DesignScheme) {
    this.atom.designSchemes.delete(designScheme);
    this.syncDesignSchemesToConcept();
  }

  @action
  setDesignSchemeForElementId(elementId: string, designScheme: DesignScheme) {
    this.atom.designSchemeIdByElementId.set(elementId, designScheme.id);
    this.syncDesignSchemesToConcept();
  }

  @action
  setDesignSchemeShadeKey(designScheme: DesignScheme, shadeKey: ShadeKey) {
    designScheme.shadeKey = shadeKey;
    this.syncDesignSchemesToConcept();
  }

  getDesignSchemeByElementId(elementId: string): DesignScheme | null {
    const schemeId = this.atom.designSchemeIdByElementId.get(elementId);
    if (schemeId != null) {
      const designScheme = this.atom.designSchemes.find(
        (s) => s.id === schemeId,
      );
      if (designScheme != null) {
        return designScheme;
      }
    }

    // Try to fall back to the first scheme.
    const designScheme = this.atom.designSchemes[0];
    if (designScheme != null) {
      return designScheme;
    }

    // Schemes probably haven't been generated yet.
    return null;
  }

  getDesignSchemeShadeKeyByElementId(elementId: string): ShadeKey {
    const shadeKey = this.getDesignSchemeByElementId(elementId)?.shadeKey;
    return shadeKey != null ? assertShadeKey(shadeKey) : 'neutral-shade-0';
  }

  getDesignSchemeNumber(designScheme: DesignScheme): number {
    const idx = this.atom.designSchemes.findIndex(
      (s) => s.id === designScheme.id,
    );
    return idx + 1;
  }

  getDesignSchemeName(designScheme: DesignScheme): string {
    return `Scheme ${this.getDesignSchemeNumber(designScheme)}`;
  }

  @action
  populateInitialDesignSchemes() {
    // Generate schemes
    const schemeShadeKeys = getSchemeShadeKeys({
      library: this.library,
      sections: this.session.project.homePage.state.sections,
      project: this.session.project.state,
      concept: this.state,
    });

    const processedSections = extractSectionMetadata(this.library, {
      sections: this.session.project.homePage.sections,
      project: this.session.project,
      getScheme: (elementId) => this.getCompiledSchemeByElementId(elementId),
      concept: this,
      uiStyle: this.uiStyle,
    });

    this.atom.designSchemes.clear();
    this.atom.designSchemes.push(
      ...schemeShadeKeys.map((shadeKey) => aDesignScheme({ shadeKey })),
    );

    this.atom.designSchemeIdByElementId.clear();
    for (const { section, scheme: compiledScheme } of processedSections) {
      const elementId = section.element.current?.id;
      if (elementId == null) {
        continue;
      }

      const designScheme = this.atom.designSchemes.find(
        (d) => d.shadeKey === getShadeKey(compiledScheme),
      );
      if (designScheme == null) {
        continue;
      }

      this.atom.designSchemeIdByElementId.set(elementId, designScheme.id);
    }
  }

  @action
  syncConceptToDesignSchemes() {
    // Remove any design schemes for which the chromatic no longer exists.
    for (const designScheme of this.atom.designSchemes) {
      const shade = parseShadeKey(designScheme.shadeKey);
      if (
        shade?.palette === 'chromatic' &&
        // note: chromatic is 1-indexed
        shade.chromatic > this.chromatics.length
      ) {
        this.atom.designSchemes.delete(designScheme);
      }
    }

    if (this.atom.designSchemes.length === 0) {
      // No design schemes to sync to.
      return;
    }

    const processedSections = extractSectionMetadata(this.library, {
      sections: this.session.project.homePage.sections,
      project: this.session.project,
      getScheme: (elementId) => this.getCompiledSchemeByElementId(elementId),
      concept: this,
      uiStyle: this.uiStyle,
    });

    for (const { section, scheme: compiledScheme } of processedSections) {
      const elementId = section.element.current?.id;
      if (elementId == null) {
        continue;
      }

      const shadeKey = getShadeKey(compiledScheme);
      const existingDesignScheme = this.getDesignSchemeByElementId(elementId);

      if (shadeKey === existingDesignScheme?.shadeKey) {
        // Concept and design haven't diverged.
        continue;
      }

      // Try to find a design scheme that already has this shade.
      let newDesignScheme = this.atom.designSchemes.find(
        (s) => s.shadeKey === shadeKey,
      );
      if (newDesignScheme == null) {
        // There aren't any design schemes with this shade yet, so create one.
        [newDesignScheme] = this.atom.designSchemes.push(
          aDesignScheme({ shadeKey }),
        );
      }

      this.atom.designSchemeIdByElementId.set(elementId, newDesignScheme.id);
    }
  }

  @action
  syncDesignSchemesToConcept() {
    for (const sref of this.session.project.homePage.sections) {
      const section = unrefSection(sref, this.session.project);
      if (section == null) {
        continue;
      }

      const elementId = section.element.current?.id;
      if (elementId == null) {
        continue;
      }

      const shadeKey = this.getDesignSchemeShadeKeyByElementId(elementId);
      if (this.atom.schemeOverrideByElementId.get(elementId) !== shadeKey) {
        this.atom.schemeOverrideByElementId.set(elementId, shadeKey);
      }
    }
  }

  @action
  ensureDesignSchemesSyncedAndApplied(assistant: Assistant) {
    if (this.atom.designSchemes.length === 0) {
      // Create initial design schemes based on the home page...
      this.populateInitialDesignSchemes();

      // ...and apply them to the other pages.
      assistant.applyDesignSchemesToPages(
        pageAndAllSubpages(this.session.project.homePage).filter(
          (p) => p !== this.session.project.homePage,
        ),
        this,
      );
    }
  }
}
